import DOMPurify from 'dompurify';

const CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'div', 'span',
    'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'mark',
    // execCommand('foreColor') sin styleWithCSS genera <font color="...">.
    'font',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'blockquote', 'pre', 'code',
    'a', 'img',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'hr'
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'title',
    'src', 'alt', 'width', 'height',
    'class', 'style',
    'colspan', 'rowspan', 'border',
    'color',
    'data-attachment-id',
    // Menciones @usuario: el span de la mención lleva data-user-id (contrato §3).
    // El saneo lo mantiene pero solo si es numérico (hook de abajo).
    'data-user-id'
  ],
  // Regex por defecto de DOMPurify acotada a http(s)/mailto/tel. Exige ':' tras
  // el esquema y deja pasar valores sin esquema (target="_blank", width="400",
  // colspan="2", rutas relativas). javascript:, vbscript: y data: quedan fuera.
  // Las data: de <img src> entran por la lista DATA_URI_TAGS de DOMPurify y el
  // hook de abajo las limita a imágenes raster.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
};

// Propiedades CSS que genera el editor (alineado, color, resaltado, tamaño de
// imagen, tablas). Cualquier otra (position, z-index, inset...) se descarta.
const ALLOWED_STYLE_PROPS = new Set([
  'text-align', 'color', 'background-color',
  'font-weight', 'font-style', 'text-decoration',
  'width', 'height', 'max-width', 'min-width',
  'border', 'border-collapse', 'padding', 'margin',
  // Presentes en contenido histórico (pegado desde Docs/Word): inofensivas y
  // sin ellas el texto antiguo cambiaría de aspecto al sanear en los visores.
  'font-size', 'font-family', 'line-height', 'list-style-type', 'vertical-align'
]);
const DANGEROUS_STYLE = /url\s*\(|expression|javascript:|@import|behavior|-moz-binding|\\/i;
const SAFE_STYLE_VALUE = /^[#a-z0-9\s.,%()+'"!-]*$/i;
const RASTER_DATA_URI = /^data:image\/(?:png|jpe?g|gif|webp);/i;

const filterStyle = (style) => {
  if (DANGEROUS_STYLE.test(style)) return '';
  return style
    .split(';')
    .map((decl) => {
      const idx = decl.indexOf(':');
      if (idx === -1) return null;
      const prop = decl.slice(0, idx).trim().toLowerCase();
      const val = decl.slice(idx + 1).trim();
      if (!ALLOWED_STYLE_PROPS.has(prop) || !val || !SAFE_STYLE_VALUE.test(val)) return null;
      return `${prop}: ${val}`;
    })
    .filter(Boolean)
    .join('; ');
};

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('rel', 'noopener noreferrer');
  }

  // data: solo como imagen raster (el editor pega imágenes en base64).
  // data:image/svg+xml y cualquier otro data: se eliminan.
  if (node.tagName === 'IMG' && node.hasAttribute('src')) {
    const src = (node.getAttribute('src') || '').trim();
    if (/^data:/i.test(src) && !RASTER_DATA_URI.test(src)) {
      node.removeAttribute('src');
    }
  }

  if (node.hasAttribute && node.hasAttribute('style')) {
    const filtered = filterStyle(node.getAttribute('style') || '');
    if (filtered) node.setAttribute('style', filtered);
    else node.removeAttribute('style');
  }

  // Menciones: data-user-id SIEMPRE numérico (contrato §3). Si no lo es,
  // se elimina el atributo (nunca permitir valores no numéricos).
  if (node.hasAttribute && node.hasAttribute('data-user-id')) {
    const uid = node.getAttribute('data-user-id');
    if (!/^\d+$/.test(uid)) {
      node.removeAttribute('data-user-id');
    }
  }
});

export function sanitizeHtml(dirty) {
  if (dirty == null) return '';
  return DOMPurify.sanitize(String(dirty), CONFIG);
}

// Texto plano de un HTML sin ejecutarlo: DOMParser crea un documento inerte
// (no lanza onerror ni carga imágenes), al contrario que div.innerHTML.
export function htmlToText(html) {
  if (!html) return '';
  try {
    return new DOMParser().parseFromString(String(html), 'text/html').body.textContent || '';
  } catch {
    return '';
  }
}

export default sanitizeHtml;
